import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, distinctUntilChanged } from 'rxjs';

/** Clave de localStorage del centro activo (sobrevive navegación y recargas). */
const STORAGE_KEY = 'gr_selected_organization';

/**
 * Centro / organización seleccionado en el selector del sidebar v3 (rol Director).
 *
 * Fuente única de verdad del centro activo. El layout v3 lo siembra desde el
 * perfil del Director y lo actualiza cuando cambia de centro; las pantallas que
 * dependen del centro (panel institucional, listado de clases) se suscriben a
 * `organizationId$` y refrescan sus datos con la organización elegida.
 *
 * Se persiste en localStorage porque cada ruta v3 recrea el `LayoutV3Component`:
 * sin persistir, navegar entre el panel y el listado de clases reiniciaría la
 * selección al primer centro del perfil.
 */
@Injectable({ providedIn: 'root' })
export class SelectedOrganizationService {
  private readonly _organizationId$ = new BehaviorSubject<number | null>(
    this.readPersisted(),
  );

  /** Id del centro activo; `null` hasta que el layout lo siembra. */
  readonly organizationId$: Observable<number | null> =
    this._organizationId$.pipe(distinctUntilChanged());

  /** Snapshot del centro activo, sin suscribirse. */
  get organizationId(): number | null {
    return this._organizationId$.value;
  }

  /** Fija el centro activo y lo persiste. No emite si no cambió. */
  select(organizationId: number): void {
    if (this._organizationId$.value === organizationId) return;
    localStorage.setItem(STORAGE_KEY, String(organizationId));
    this._organizationId$.next(organizationId);
  }

  /** Limpia el centro activo (p. ej. al cerrar sesión). */
  clear(): void {
    localStorage.removeItem(STORAGE_KEY);
    this._organizationId$.next(null);
  }

  private readPersisted(): number | null {
    const raw = localStorage.getItem(STORAGE_KEY);
    const id = raw ? Number(raw) : NaN;
    return Number.isFinite(id) ? id : null;
  }
}
